"use strict";

const _ = require("lodash");

class InputTextControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("input-text-control");
        this._textField = document.createElement("input");
        this._textField.setAttribute("type", "text");
        this._textField.setAttribute("autocomplete", this._model.autocompleteHint);
        this._errorSpan = document.createElement("span");
        this._errorSpan.classList.add("error");
        this._rootElem.appendChild(this._textField);
        this._rootElem.appendChild(this._errorSpan);
        this._rootElem.component = this._model;

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*, reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._textField = undefined;
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("Placeholder", (this._onPlaceholder = this._onPlaceholder.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on(
            "AutocompleteHint",
            (this._onAutocompleteHint = this._onAutocompleteHint.bind(this))
        );
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._model.on("Title", (this._onTitle = this._onTitle.bind(this)));
        this._model.on(
            "PatternMatchAll",
            (this._onPatternMatchAll = this._onPatternMatchAll.bind(this))
        );
        this._model.on("Pattern", (this._onPattern = this._onPattern.bind(this)));
        this._model.on("Required", (this._onRequired = this._onRequired.bind(this)));
        this._model.on(
            "ValidationVerbiage",
            (this._onValidationVerbiage = this._onValidationVerbiage.bind(this))
        );
        this._textField.addEventListener(
            "input",
            (this._onInputEvent = this._onInputEvent.bind(this))
        );
        this._textField.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._textField.addEventListener(
            "blur",
            (this._onBlurEvent = this._onBlurEvent.bind(this))
        );
        this._textField.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("Placeholder", this._onPlaceholder);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("AutocompleteHint", this._onAutocompleteHint);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._model.removeListener("Title", this._onTitle);
        this._model.removeListener("PatternMatchAll", this._onPatternMatchAll);
        this._model.removeListener("Pattern", this._onPattern);
        this._model.removeListener("Required", this._onRequired);
        this._model.removeListener("ValidationVerbiage", this._onValidationVerbiage);
        this._textField.removeEventListener("input", this._onInputEvent);
        this._textField.removeEventListener("focus", this._onFocusEvent);
        this._textField.removeEventListener("blur", this._onBlurEvent);
        this._textField.removeEventListener("keydown", this._onKeyDownEvent);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*, reject*/) => {
            let isInvalid = this._model.invalid;
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setPlaceholder(this._model.placeholder);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            this._setTitle(this._model.title);
            this._setPattern(this._model.pattern);
            this._setRequired(this._model.required);
            this._setInvalid(isInvalid || !this._textField.validity.valid);
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    /**
     * Returns validation messages based on the validity of text value on this control
     * @returns {Array}
     */
    getViolations() {
        let validationMessages = this._textField.validity.valueMissing
            ? [this._model._validationVerbiage.required]
            : [];
        if (this._textField.validity.patternMismatch) {
            validationMessages.push(this._model._validationVerbiage.pattern);
        }
        return validationMessages;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the placeholder has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onPlaceholder(newVal) {
        this._setPlaceholder(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when setAutocompleteHint is called on the model
     * @param newVal
     * @private
     */
    _onAutocompleteHint(newVal) {
        this._setAutocompleteHint(newVal);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Fired when setTitle has been called on the model
     * @param newVal {String} - title to be set on the control
     * @private
     */
    _onTitle(newVal) {
        this._setTitle(newVal);
    }

    /**
     * Fired when patternMatchAll has been updated on the model
     * @param newVal {boolean} - patternMatchAll decides how pattern matches input text on the control
     * @private
     */
    _onPatternMatchAll(newVal) {
        this._setPatternMatchAll(newVal);
    }

    /**
     * Fired when setPattern has been called on the model
     * @param newVal {String} - regular expression to to validate input text value
     * @private
     */
    _onPattern(newVal) {
        this._setPattern(newVal);
    }

    _onRequired(newRequiredVal) {
        this._setRequired(newRequiredVal);
    }

    /**
     * Fired when valiation Messages verbiage is updated on the model
     * @param newValidationVerbiage
     * @private
     */
    _onValidationVerbiage(newValidationVerbiage) {
        this._setValidationVerbiage(newValidationVerbiage);
    }

    /**
     * Update the value in the model based on user input
     * @param event {Event}
     * @private
     */
    _onInputEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        let newVal = this._textField.value;
        this._model.setValue(newVal, true);
        this._setInvalid(!this._textField.validity.valid);
        this._model._triggerUserValue(newVal, oldVal, event);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        this._model._triggerUserKeyDown(event);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal === true) {
            this._textField.disabled = true;
        } else {
            this._textField.disabled = false;
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newInvalidStatus) {
        let oldVal = _.clone(this._textField.value),
            self = this;
        let oldInvalidStatus = this._model.invalid;
        if (newInvalidStatus === true) {
            this._model._invalid = true;
            this._rootElem.classList.add("invalid");
            let validationMessages = this.getViolations();
            if (this.model.showErrorMessage === true) {
                if (this.model.useNativeValidation === false) {
                    this._errorSpan.textContent = validationMessages.join("\n");
                    this._rootElem.classList.add("showError");
                } else {
                    setTimeout(() => {
                        if (self._textField.value === oldVal) {
                            self._textField.setCustomValidity(validationMessages.join("\n"));
                            self._textField.reportValidity();
                        }
                    }, 1000);
                }
            }
        } else {
            this._model._invalid = false;
            this._rootElem.classList.remove("invalid");
            if (this._model.showErrorMessage === true) {
                this._rootElem.classList.remove("showError");
                this._textField.setCustomValidity("");
            }
        }
        if (oldInvalidStatus !== newInvalidStatus) {
            this._model._triggerInvalidStatusEvent(newInvalidStatus, oldInvalidStatus);
        }
    }

    /**
     * Put the placeholder text in place and move the cursor to the front of the selection if placeholder text
     * is displaying.
     * @param newVal {String}
     * @private
     */
    _setPlaceholder(newVal) {
        if (newVal !== undefined && newVal !== null && newVal !== "") {
            this._textField.placeholder = newVal;
        } else {
            this._textField.placeholder = "";
        }
    }

    /**
     * Toggle the display text
     * @param newVal {String}
     * @private
     */
    _setValue(newVal) {
        let inputVal = newVal;
        if (inputVal !== this._textField.value) {
            if (newVal) {
                this._textField.value = inputVal;
            } else {
                this._textField.value = null;
                if (this._model.value !== null) {
                    this._model.value = null;
                }
            }
            this._setInvalid(!this._textField.validity.valid);
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("dark");
        } else {
            this._rootElem.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set the title/tooltip
     * @param newVal {String}
     * @private
     */
    _setTitle(newVal) {
        if (newVal) {
            this._rootElem.setAttribute("title", newVal);
        } else {
            this._rootElem.setAttribute("title", "");
        }
    }

    _setPatternMatchAll(/**newVal*/) {
        this._setPattern(this._model.pattern);
    }

    _setPattern(newVal) {
        if (newVal) {
            let pattern = this._getModifiedPattern(this._model.pattern);
            this._textField.setAttribute("pattern", pattern);
        } else {
            this._textField.removeAttribute("pattern");
        }
        this._setInvalid(!_.get(this._textField, "validity.valid", true));
    }

    _setRequired(newRequiredVal) {
        if (newRequiredVal === true) {
            this._textField.setAttribute("required", "");
        } else {
            this._textField.removeAttribute("required");
        }
        this._setInvalid(!this._textField.validity.valid);
    }

    /**
     * Update validation messages verbiage displayed on UI in case of validation errors
     * @param newValidationVerbiage
     * @private
     */
    _setValidationVerbiage() {
        this._setInvalid(!this._textField.validity.valid);
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._textField.focus();
        } else {
            this._textField.blur();
        }
    }

    /**
     * Set autocomplete on the textfield
     * @param newVal {string}
     * @private
     */
    _setAutocompleteHint(newVal) {
        this._setAttribute("autocomplete", newVal);
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._textField.setAttribute(attrName, attrValue);
        } else {
            this._textField.removeAttribute(attrName);
        }
    }

    /**
     * Checks if we only part of the pattern apply
     * @param {String} pattern - pattern supports only string
     */
    _getModifiedPattern(pattern) {
        let newPattern = pattern;
        if (!this._model.patternMatchAll && pattern) {
            newPattern = newPattern.startsWith("^") ? newPattern : ".*" + newPattern;
            newPattern = newPattern.endsWith("$") ? newPattern : newPattern + ".*";
        }
        return newPattern;
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = InputTextControlRenderer;
